import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type {
  ProspectAddLeadsResult,
  ProspectCampaign,
  ProspectDecisionResult,
  ProspectImportResult,
  ProspectKillSwitch,
  ProspectLead,
  ProspectMessage,
  ProspectorPage,
  ProspectRun,
  ProspectSpend,
  ProspectSuppressionResult,
  ProspectTarget,
} from "../prospector-types";

type ProspectorApiMethods = Pick<
  ApiClient,
  | "importProspectLeads"
  | "getProspectorEnabled"
  | "listProspectTargets"
  | "createProspectTarget"
  | "updateProspectTarget"
  | "deleteProspectTarget"
  | "runProspectTarget"
  | "listProspectRuns"
  | "listProspectLeads"
  | "getProspectLead"
  | "updateProspectLeadStatus"
  | "suppressProspectLead"
  | "getProspectSpend"
  | "listProspectCampaigns"
  | "createProspectCampaign"
  | "updateProspectCampaign"
  | "archiveProspectCampaign"
  | "addProspectCampaignLeads"
  | "listProspectMessages"
  | "approveProspectMessage"
  | "rejectProspectMessage"
  | "bulkApproveProspectMessages"
  | "bulkRejectProspectMessages"
  | "getProspectKillSwitch"
  | "setProspectKillSwitch"
>;

/** Query string from the defined entries only (`""` when there are none). */
function toQuery(params: Record<string, string | number | undefined> | undefined): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : "";
}

export function createProspectorApi({ request, ws }: HttpTransport): ProspectorApiMethods {
  const root = (id: string) => `${ws(id)}/prospecting`;
  const one = (targetId: string) => encodeURIComponent(targetId);
  return {
    importProspectLeads: async (id, input) =>
      (await request<{ data: ProspectImportResult }>("POST", `${root(id)}/leads/import`, input))
        .data,
    getProspectorEnabled: async (id) =>
      (await request<{ enabled: boolean }>("GET", `${root(id)}/enabled`)).enabled,
    listProspectTargets: async (id) =>
      (await request<{ data: ProspectTarget[] }>("GET", `${root(id)}/targets`)).data,
    createProspectTarget: async (id, input) =>
      (await request<{ data: ProspectTarget }>("POST", `${root(id)}/targets`, input)).data,
    updateProspectTarget: async (id, targetId, patch) =>
      (
        await request<{ data: ProspectTarget }>(
          "PATCH",
          `${root(id)}/targets/${one(targetId)}`,
          patch,
        )
      ).data,
    deleteProspectTarget: async (id, targetId) =>
      (await request<{ data: ProspectTarget }>("DELETE", `${root(id)}/targets/${one(targetId)}`))
        .data,
    runProspectTarget: async (id, targetId, idempotencyKey) =>
      (
        await request<{ data: ProspectRun[] }>(
          "POST",
          `${root(id)}/targets/${one(targetId)}/runs`,
          {},
          undefined,
          idempotencyKey ? { "idempotency-key": idempotencyKey } : undefined,
        )
      ).data,
    listProspectRuns: (id, query) =>
      request<ProspectorPage<ProspectRun>>("GET", `${root(id)}/runs${toQuery({ ...query })}`),
    listProspectLeads: (id, query) =>
      request<ProspectorPage<ProspectLead>>("GET", `${root(id)}/leads${toQuery({ ...query })}`),
    getProspectLead: async (id, leadId) =>
      (await request<{ data: ProspectLead }>("GET", `${root(id)}/leads/${one(leadId)}`)).data,
    updateProspectLeadStatus: async (id, leadId, status) =>
      (
        await request<{ data: ProspectLead }>("PATCH", `${root(id)}/leads/${one(leadId)}`, {
          status,
        })
      ).data,
    suppressProspectLead: async (id, input) =>
      (
        await request<{ data: ProspectSuppressionResult }>(
          "POST",
          `${root(id)}/suppressions`,
          input,
        )
      ).data,
    getProspectSpend: async (id, currency) =>
      (await request<{ data: ProspectSpend }>("GET", `${root(id)}/spend${toQuery({ currency })}`))
        .data,
    listProspectCampaigns: async (id) =>
      (await request<ProspectorPage<ProspectCampaign>>("GET", `${root(id)}/campaigns`)).data,
    createProspectCampaign: async (id, input) =>
      (await request<{ data: ProspectCampaign }>("POST", `${root(id)}/campaigns`, input)).data,
    updateProspectCampaign: async (id, campaignId, patch) =>
      (
        await request<{ data: ProspectCampaign }>(
          "PATCH",
          `${root(id)}/campaigns/${one(campaignId)}`,
          patch,
        )
      ).data,
    archiveProspectCampaign: async (id, campaignId) =>
      (
        await request<{ data: ProspectCampaign }>(
          "DELETE",
          `${root(id)}/campaigns/${one(campaignId)}`,
        )
      ).data,
    addProspectCampaignLeads: async (id, campaignId, leadIds) =>
      (
        await request<{ data: ProspectAddLeadsResult }>(
          "POST",
          `${root(id)}/campaigns/${one(campaignId)}/leads`,
          { leadIds },
        )
      ).data,
    listProspectMessages: (id, query) =>
      request<ProspectorPage<ProspectMessage>>(
        "GET",
        `${root(id)}/messages${toQuery({ ...query })}`,
      ),
    approveProspectMessage: async (id, messageId) =>
      (
        await request<{ data: ProspectMessage }>(
          "POST",
          `${root(id)}/messages/${one(messageId)}/approve`,
          {},
        )
      ).data,
    rejectProspectMessage: async (id, messageId) =>
      (
        await request<{ data: ProspectMessage }>(
          "POST",
          `${root(id)}/messages/${one(messageId)}/reject`,
          {},
        )
      ).data,
    bulkApproveProspectMessages: async (id, messageIds) =>
      (
        await request<{ data: ProspectDecisionResult }>("POST", `${root(id)}/messages/approve`, {
          messageIds,
        })
      ).data,
    bulkRejectProspectMessages: async (id, messageIds) =>
      (
        await request<{ data: ProspectDecisionResult }>("POST", `${root(id)}/messages/reject`, {
          messageIds,
        })
      ).data,
    getProspectKillSwitch: async (id) =>
      (await request<{ data: ProspectKillSwitch }>("GET", `${root(id)}/kill-switch`)).data,
    setProspectKillSwitch: async (id, active) =>
      (await request<{ data: ProspectKillSwitch }>("PUT", `${root(id)}/kill-switch`, { active }))
        .data,
  };
}
